import { AuthError } from '@sysdynetechnologies/sysdyne-id-client-frontend';
import { SysdyneIdPlugin } from '../../../../libs/sysdyne-id-client/react/types';
import logger from '../../../utils/logger';
import RootStore from '../../../stores/root-store';
import { logTokenRefreshEvent } from '../utils/log-token-refresh-event';
import { NETWORK_EVENT } from '../../../stores/connection-store';
import { User } from '../../../../types/api/User';

export interface TokenInfo {
  accessToken: string;
  refreshToken: string;
  expiresAt: number;
}

export class TokenRefreshManager {
  private refreshTimer: number | null = null;
  private refreshInProgress = false;
  private failedRefreshAttempts = 0;
  private sysdyneIdPlugin: SysdyneIdPlugin;
  private rootStore: RootStore;
  private tokenInfo: TokenInfo | null = null;
  private onTokenRefreshed: (tokenInfo: TokenInfo) => void;
  private onTokenExpired: () => void;
  private user: User | null;

  // Handler for retrying refresh when connection is restored
  private handleConnectionRestored = () => {
    window.removeEventListener(NETWORK_EVENT.CONNECTION_GOOD, this.handleConnectionRestored);
    void this.attemptTokenRefresh();
  };

  constructor(
    sysdyneIdPlugin: SysdyneIdPlugin,
    rootStore: RootStore,
    onTokenRefreshed: (tokenInfo: TokenInfo) => void,
    onTokenExpired: () => void,
    initialTokenInfo?: TokenInfo,
    user?: User | null,
  ) {
    this.sysdyneIdPlugin = sysdyneIdPlugin;
    this.rootStore = rootStore;
    this.onTokenRefreshed = onTokenRefreshed;
    this.onTokenExpired = onTokenExpired;
    this.tokenInfo = initialTokenInfo || null;
    this.user = user || null;
  }

  updateUser(user: User | null) {
    this.user = user;
  }

  updateTokenInfo(tokenInfo: TokenInfo) {
    this.tokenInfo = tokenInfo;
    this.setupTokenRefreshTimer();
  }

  setupTokenRefreshTimer() {
    this.clearRefreshTimer();

    if (!this.tokenInfo?.expiresAt) {
      logger.warn('Token expiration time not set, skipping refresh timer setup');
      return;
    }

    // tokenExpiresAt is in seconds
    const now = Date.now() / 1000;
    const secondsUntilRefresh = this.tokenInfo.expiresAt - now;

    if (secondsUntilRefresh <= 0) {
      // Token is already expired or will expire very soon
      void this.attemptTokenRefresh();
      return;
    }

    const millisecondsUntilRefresh = secondsUntilRefresh * 1000;

    logger.debug(`Setting up token refresh timer for ${millisecondsUntilRefresh}ms from now`);
    this.refreshTimer = this.rootStore.asyncStore.setTimeout(() => {
      void this.attemptTokenRefresh();
    }, millisecondsUntilRefresh);
  }

  clearRefreshTimer() {
    if (this.refreshTimer) {
      this.rootStore.asyncStore.clearTimeout(this.refreshTimer);
      this.refreshTimer = null;
    }
  }

  async handleTokenRefreshError(error: any) {
    logTokenRefreshEvent('handle-error', {
      userId: this.user?.id,
      username: this.user?.name,
      plant: this.rootStore.locationsStore.currentPlant?.description,
      error: error instanceof Error ? error.message : String(error),
      stack: error instanceof Error ? error.stack : undefined,
    });

    const { notifierStore } = this.rootStore;
    if (error instanceof AuthError) {
      notifierStore.showError('Failed to refresh token. Please log in again.', 10000);
      await this.sysdyneIdPlugin.logout({ redirect: false });
      // Notify store/UI that the token is definitively expired
      this.onTokenExpired();
      if (!this.user) {
        logger.log('[TokenRefresh] Reloading page...');
        window.location.reload();
      }
    }

    this.clearRefreshTimer();
    logger.error('[TokenRefresh] Error during token refresh', error);
  }

  async attemptTokenRefresh() {
    // Prevent refresh when offline: schedule retry when connection is restored
    const isOnline = this.rootStore.connectionStore.isOnline;
    if (!isOnline) {
      logger.warn('[TokenRefresh] Offline detected, skipping refresh until connection is restored');
      logTokenRefreshEvent('skip-refresh-offline', {
        userId: this.user?.id,
        username: this.user?.name,
        plant: this.rootStore.locationsStore.currentPlant?.description,
      });
      // Clear any existing timer and wait for connection to come back
      this.clearRefreshTimer();
      window.addEventListener(NETWORK_EVENT.CONNECTION_GOOD, this.handleConnectionRestored);
      return;
    }
    if (this.refreshInProgress) {
      logger.warn('[TokenRefresh] Refresh already in progress, skipping duplicate call');
      return;
    }
    this.refreshInProgress = true;
    try {
      logger.warn('[TokenRefresh] Token has expired. Attempting refresh...');
      const { notifierStore } = this.rootStore;

      if (!this.tokenInfo?.accessToken || !this.tokenInfo?.refreshToken) {
        logTokenRefreshEvent('attempt-refresh-error', {
          userId: this.user?.id,
          username: this.user?.name,
          plant: this.rootStore.locationsStore.currentPlant?.description,
          hasAuthToken: !!this.tokenInfo?.accessToken,
          hasRefreshToken: !!this.tokenInfo?.refreshToken,
          error: 'Auth token is not set. Ignoring refresh request',
        });
        throw new Error('Auth token is not set. Ignoring refresh request');
      }

      let authToken;
      const forceRefresh = this.failedRefreshAttempts >= 2;
      try {
        if (forceRefresh) {
          logTokenRefreshEvent('force-refresh', {
            userId: this.user?.id,
            username: this.user?.name,
            failedRefreshAttempts: this.failedRefreshAttempts,
            plant: this.rootStore.locationsStore.currentPlant?.description,
          });
          logger.warn('[TokenRefresh] Plugin failed to return valid token. Forcing token refresh...');
        }
        authToken = await this.sysdyneIdPlugin.getToken(forceRefresh);
      } catch (error) {
        logger.error(forceRefresh ? '[TokenRefresh] Forced token refresh failed' : '[TokenRefresh] Token refresh failed', error);
        await this.handleTokenRefreshError(error);
        return;
      }

      // Check if the plugin returned a new token
      if (
        this.tokenInfo?.accessToken === authToken?.accessToken &&
        this.tokenInfo?.refreshToken === authToken?.refreshToken &&
        this.tokenInfo?.expiresAt === authToken?.expiresAt
      ) {
        logger.warn('[TokenRefresh] Plugin did not return a new token.');
        this.failedRefreshAttempts++;
        return;
      }

      if (
        authToken?.accessToken &&
        authToken?.refreshToken &&
        authToken?.expiresAt
      ) {
        logger.log('[TokenRefresh] Token refresh successful');
        this.failedRefreshAttempts = 0;
        const newTokenInfo = {
          accessToken: authToken.accessToken,
          refreshToken: authToken.refreshToken,
          expiresAt: authToken.expiresAt,
        };
        this.tokenInfo = newTokenInfo;
        this.onTokenRefreshed(newTokenInfo);
        this.setupTokenRefreshTimer();
      } else {
        logger.error('[TokenRefresh] Token refresh failed');
        notifierStore.showError('Your session has expired. Please log in again.', 10000);
        this.onTokenExpired();
        this.clearRefreshTimer();
      }
    } finally {
      this.refreshInProgress = false;
    }
  }
}
